import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import focusable from './components/Focusable/Focusable'
import { Navigate, Routes, Route } from 'react-router-dom'
import Deeplink from './components/Deeplink'
import Screensaver from './components/Screensaver/Screensaver.js'
import NetworkDetector from './NetworkDetector.js'
import './App.scss'
import SplashScreen from './screens/SplashScreen/SplashScreen.js'
import ActivationScreen from './screens/ActivationScreen'
import ActivationCodeScreen from './screens/ActivationCodeScreen'
import HomeScreen from './screens/HomeScreen/HomeScreen.js'
import ShowsScreen from './screens/ShowsScreen/ShowsScreen.js'
import MyStationScreen from './screens/MyStationScreen/MyStationScreen.js'
import PassPortUpsell from './screens/PassPortUpsell/PassPortUpsell.js'
import VideoDetailScreen from './screens/VideoDetailScreen/VideoDetailScreen.js'
import ShowDetailsScreen from './screens/ShowDetailsScreen/ShowDetailsScreen.js'
import VideoPlaybackScreen from './screens/VideoPlaybackScreen/VideoPlaybackScreen'
import SeasonsScreen from './screens/SeasonsScreen/SeasonsScreen.js'
import CollectionsScreen from './screens/CollectionsScreen/CollectionsScreen.js'
import ChangeStationScreen from './screens/ChangeStationScreen/ChangeStationScreen.js'
import SignoutScreen from './screens/SignoutScreen/SignoutScreen.js'
import MyListScreen from './screens/MyListScreen/MyListScreen.js'
import SearchScreen from './screens/SearchScreen'
import SeeAllScreen from './screens/SeeAllScreen/SeeAllScreen.js'
import ErrorScreen from './screens/ErrorScreen/ErrorScreen.js'
import InAppDonationScreen from './screens/InAppDonationScreen/InAppDonationScreen'
import LearnMoreScreen from './screens/LearnMoreScreen/LearnMoreScreen.js'
import { triggerSentryError, ComcastErrorType } from '../../shared/helpers.js'
import { captureException } from '@sentry/browser'
import { store } from './store'
import { setLoadingState } from './lib/ui'
import PassportActivationScreen from './screens/PassportActivationScreen/PassportActivationScreen'
import LivestreamScreen from './screens/Livestream/LivestreamScreen.tsx'
import LivePlayerScreen from './screens/LivePlayerScreen/LivePlayerScreen'
import LivePlayerError from './screens/LivePlayerScreen/LiveError'
import CancellationScreen from './screens/DonationCancelationScreen'
import { showDonationFlag } from './lib/donationFlagLogger.ts'
import { SidePanelLayout, SidePanelProvider } from './components/SidePanel'
import KeyDownProvider from '../../shared/providers/KeyDownProvider'
import SettingsScreen from './screens/SettingsScreen'

class SentryBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  componentDidCatch(error, errorInfo) {
    triggerSentryError(
      `
      'App.js ${error.name}`,
      'App.js',
      {
        errorMessage: error.message ?? 'Undefined error message (default)',
        errorLocation: 'App (SentryBoundary) componentDidCatch',
        errorInfo,
        errorType: ComcastErrorType.other,
      },
      true
    )

    setLoadingState(false)
    store.dispatch({
      type: 'TRIGGER_ERROR',
      payload: {
        error: {
          status: 'fatal',
          content: {
            errorPath: undefined,
          },
          codeBaseLocation: 'App - SentryBoundary',
        },
      },
    })

    if (COMPILING_FOR_COMCASTX1) {
      import('@firebolt-js/sdk').then((firebolt) => {
        const Lifecycle = firebolt?.Lifecycle
        Lifecycle.close(Lifecycle.CloseReason.ERROR)
      })
    }

    this.setState({ error })
  }

  render() {
    if (this.state.error) {
      return <ErrorScreen />
    } else {
      //when there's not an error, render children untouched
      return this.props.children
    }
  }
}

SentryBoundary.propTypes = {
  children: PropTypes.element,
}

export class App extends React.Component {
  constructor(props) {
    super(props)
  }

  componentDidCatch(e) {
    // if sentryboundary doesn't catch the error, this will catch and still have a blue screen
    // for now lets just capture it. if it still happens, we can add another error screen or fix the issue at that point
    captureException(e)
  }
  componentDidMount() {
    this.props?.setFocus()
  }

  componentDidUpdate(prevProps) {
    if (COMPILING_FOR_COMCASTX1) {
      showDonationFlag(prevProps.overlay.enabled, this.props.overlay.enabled)
    }
  }

  onKeyEvent(key) {
    if (key === 'BACK') {
      window.history.back()
    }

    return true
  }

  render() {
    return (
      <div aria-hidden="true" className={'app'} id="app">
        <SentryBoundary>
          <KeyDownProvider>
            <>
              <Deeplink />
              <Screensaver />
              <SidePanelProvider>
                <Routes>
                  <Route path="/" element={<SplashScreen />} />
                  <Route path="/activate" element={<ActivationScreen />} />
                  <Route
                    path="/activatecode"
                    element={<ActivationCodeScreen />}
                  />
                  <Route element={<SidePanelLayout />}>
                    <Route path="/home" element={<HomeScreen />} />
                    <Route path="/livestream" element={<LivestreamScreen />} />
                    <Route path="/shows" element={<ShowsScreen />} />
                    <Route path="/my-list" element={<MyListScreen />} />
                    <Route path="/my-station" element={<MyStationScreen />} />
                    <Route path="/search" element={<SearchScreen />} />
                    <Route path="/settings" element={<SettingsScreen />} />
                  </Route>
                  <Route
                    path="/passport-upsell/:callsign"
                    element={<PassPortUpsell />}
                  />
                  <Route
                    path="/video-details/:url"
                    element={<VideoDetailScreen />}
                  />
                  <Route
                    path="/show-details/:slug"
                    element={<ShowDetailsScreen />}
                  />
                  <Route
                    path="/video-playback/:url"
                    element={<VideoPlaybackScreen />}
                  />
                  <Route path="/seasons/:show" element={<SeasonsScreen />} />
                  <Route
                    path="/collections/:url"
                    element={<CollectionsScreen />}
                  />
                  <Route
                    path="/change-station"
                    element={<ChangeStationScreen />}
                  />
                  <Route path="/sign-out" element={<SignoutScreen />} />
                  <Route
                    path="/seeall/:header/:url"
                    element={<SeeAllScreen />}
                  />
                  <Route path="/error" element={<ErrorScreen />} />
                  <Route
                    path="/in-app-donations/*"
                    element={<InAppDonationScreen />}
                  />
                  <Route path="/learn-more" element={<LearnMoreScreen />} />
                  <Route
                    path="/passport-activation"
                    element={<PassportActivationScreen />}
                  />
                  <Route
                    path="/live-playback/error"
                    element={<LivePlayerError />}
                  />
                  <Route path="/live-playback" element={<LivePlayerScreen />} />
                  <Route
                    path="/manage-donation/*"
                    element={<CancellationScreen />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </SidePanelProvider>
            </>
          </KeyDownProvider>
        </SentryBoundary>
      </div>
    )
  }
}

App.propTypes = {
  setFocus: PropTypes.func,
  user: PropTypes.object,
  overlay: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    content: state.content,
    overlay: state.overlay,
  }
}

export default connect(
  mapStateToProps,
  null,
  null
)(focusable(NetworkDetector(App)))
